import type { Operation } from '@apollo/client';
import type { GraphQLFormattedError } from 'graphql/error';

import { extractOperationName } from './extractOperationName.ts';
import { type HandlersConfigMap, type NestedError, NestedErrorCode, type ParseGraphQLErrorsArgs } from './types.ts';

const makeHandlersRunner =
  (handlersConfigMap: HandlersConfigMap, operation: Operation) =>
  (error: NestedError): boolean[] => {
    const currentOperationName = extractOperationName(operation.query);

    const handlers = handlersConfigMap.get(currentOperationName) ?? [];

    return handlers.map(handler => handler(error));
  };
type SingleGQLError = GraphQLFormattedError & {
  readonly extensions?: {
    code?: string;
    errors?: NestedError[];
    exception?: {
      stacktrace?: string[];
    };
  };
};

export const runLocalHandlers = ({
  graphQLErrors,
  handlersConfigMap,
  operation,
}: ParseGraphQLErrorsArgs): boolean[] => {
  // TODO: UIG-540 Remove this once DV360 error is handled by the backend and we can remove this check
  // current error code is "INTERNAL_SERVER_ERROR", should be "BAD_USER_INPUT"
  const dv360BuyerError = /400 Bad Request.*DV360.*single buyer/g;
  const dealNotFoundErrorMessage = /deal_by_pk/g;
  const audienceNameUniquenessError = /audience_account_id_name_key/;

  const mapBaseErrors = (error: SingleGQLError) => ({
    code: error.extensions?.code,
    errors: error.extensions?.errors,
    exception: error.extensions?.exception,
    message: error.message,
  });

  const filteredErrors = graphQLErrors
    .map(mapBaseErrors)
    .filter(({ code, errors }) => code === 'BAD_USER_INPUT' && !!errors?.length);

  const dv360Error = graphQLErrors
    .map(mapBaseErrors)
    .filter(
      ({ errors, code, exception }) =>
        code === 'INTERNAL_SERVER_ERROR' &&
        !errors?.length &&
        (exception?.stacktrace?.[0].search(dv360BuyerError) ?? -1) > 0
    )
    .map(({ code, exception }) => ({
      code,
      exception,
      errors: [
        {
          code: NestedErrorCode.VALIDATION_ERROR,
          message: 'Demand Partner DV360 can only have 1 (one) seat ID.',
          path: 'demandPartner',
        },
      ] as NestedError[],
    }));

  const dealNotFound = graphQLErrors
    .map(mapBaseErrors)
    .filter(
      ({ errors, code, exception }) =>
        code === 'INTERNAL_SERVER_ERROR' &&
        !errors?.length &&
        (exception?.stacktrace?.[1].search(dealNotFoundErrorMessage) ?? -1) > 0
    )
    .map(({ code, exception }) => ({
      code,
      exception,
      errors: [
        {
          code: NestedErrorCode.VALIDATION_ERROR,
          message: 'Deal not found',
          path: 'getDeal',
        },
      ] as NestedError[],
    }));

  const audienceNameConflict = graphQLErrors
    .map(mapBaseErrors)
    .filter(
      ({ errors, code, message }) =>
        code === 'INTERNAL_SERVER_ERROR' && !errors?.length && audienceNameUniquenessError.test(message ?? '')
    )
    .map(({ code, exception }) => ({
      code,
      exception,
      errors: [
        {
          code: NestedErrorCode.JOINT_UNIQUENESS_VIOLATION,
          message: 'Audience with such name already exists',
          path: 'objects.name',
        },
      ] as NestedError[],
    }));

  const errorMap = [...filteredErrors, ...dv360Error, ...dealNotFound, ...audienceNameConflict].flatMap(({ errors }) =>
    (errors ?? []).flatMap(makeHandlersRunner(handlersConfigMap, operation))
  );

  return errorMap;
};
